import { CmsBuilderCanvasController } from "../cms-builder/CmsBuilderCanvasController";
import { CmsActionClient } from "../cms-builder/CmsActionClient";
import { CmsInspectorController } from "../cms-builder/CmsInspectorController";
import { CmsBuilderShell } from "../cms-builder/components/CmsBuilderShell";
import { CmsInspectorPanel } from "../cms-builder/components/CmsInspectorPanel";
import type { CmsElementTypeMeta, CmsMenuItem } from "../cms-builder/types";
import { initCmsMenuTree } from "./CmsMenuTree";
import { initCmsTemplatePicker } from "./CmsTemplatePicker";
import { Modal } from "../../utils/Modal";
import { Toast } from "../../utils/Toast";

class CmsSiteBuilder {
    readonly #root: HTMLElement;
    readonly #tplCanvas: string;
    readonly #tplTree: string;
    readonly #tplPreview: string;
    readonly #tplItemAdd: string;
    readonly #tplPageInspector: string;
    readonly #componentPickerUrl: string;
    readonly #historyUrl: string;
    readonly #elementTypes: Record<string, string>;
    readonly #elementTypeMeta: Record<string, CmsElementTypeMeta>;
    readonly #actionClient: CmsActionClient;
    readonly #canvas: CmsBuilderCanvasController | null;
    readonly #inspector: CmsInspectorController;
    readonly #shell: CmsBuilderShell;
    readonly #inspectorPanel: CmsInspectorPanel;
    #menuId: number;
    #pageId: number;
    #activeTarget: string;
    #activeLocale: string;

    constructor(root: HTMLElement) {
        this.#root = root;
        this.#tplCanvas = root.dataset.tplCanvas ?? "";
        this.#tplTree = root.dataset.tplTree ?? "";
        this.#tplPreview = root.dataset.tplPreview ?? "";
        this.#tplItemAdd = root.dataset.tplItemAdd ?? "";
        this.#tplPageInspector = root.dataset.tplPageInspector ?? "";
        this.#componentPickerUrl = root.dataset.componentPickerUrl ?? "";
        this.#historyUrl = root.dataset.cmsHistoryUrl ?? "";
        this.#elementTypes = this.#filterElementTypes(this.#parseElementTypes(root.dataset.elementTypes ?? "{}"));
        this.#elementTypeMeta = this.#parseElementTypeMeta(root.dataset.elementTypeMeta ?? "{}");
        this.#actionClient = new CmsActionClient(root);
        this.#menuId = parseInt(root.dataset.menuId ?? "0", 10) || 0;
        this.#pageId = parseInt(root.dataset.pageId ?? "0", 10) || 0;
        this.#activeTarget = this.#pageId > 0 ? `page:${this.#pageId}` : "";
        const stored = sessionStorage.getItem("cms.builder.locale");
        this.#activeLocale = stored || (root.dataset.defaultLocale ?? "ko");

        this.#inspector = new CmsInspectorController(root, {
            onSaved: () => {
                void this.#reloadCanvas();
                void this.#reloadTree();
            },
            onPageCreated: (pageId) => {
                void this.#reloadTree();
                if (pageId > 0) void this.#loadCanvas(pageId);
            },
            onGroupSaved: (redirect) => {
                if (redirect) {
                    window.location.assign(redirect);
                    return;
                }
                window.location.assign(this.#builderUrl());
            },
            getPageInspectorUrl: (pageId) => this.#tplPageInspector.replace("__ID__", String(pageId)),
        }, this.#activeLocale);
        this.#shell = new CmsBuilderShell(root, {
            onMenuAction: (item, trigger) => { void this.#handleShellMenuAction(item, trigger); },
        });
        this.#inspectorPanel = new CmsInspectorPanel(root);

        const canvasHost = this.#q("[data-cms-canvas-host]");
        this.#canvas = canvasHost
            ? new CmsBuilderCanvasController(root, canvasHost, this.#tplCanvas, {
                openInspector: (url, title) => { void this.#guard(() => { void this.#inspector.open(url, title); }); },
                reloadTree: () => { void this.#reloadTree(); },
                getLocale: () => this.#activeLocale,
                getComponentPickerUrl: () => this.#componentPickerUrl,
            }, this.#elementTypes, this.#elementTypeMeta)
            : null;
    }

    init(): void {
        initCmsMenuTree();
        this.#shell.init();
        this.#inspectorPanel.init();
        this.#inspector.init();
        this.#canvas?.init();
        this.#applyLocaleButtons();
        this.#syncPreviewButton();
        this.#highlightActivePage();

        this.#q<HTMLSelectElement>("[data-cms-group-select]")?.addEventListener("change", (event) => {
            const value = parseInt((event.currentTarget as HTMLSelectElement).value, 10) || 0;
            void this.#switchGroup(value);
        });

        this.#q("[data-cms-add-item]")?.addEventListener("click", (event) => {
            const url = (event.currentTarget as HTMLElement).dataset.url ?? "";
            if (url) void this.#guard(() => { void this.#inspector.open(url, "Add item"); });
        });
        this.#q("[data-cms-group-add]")?.addEventListener("click", (event) => {
            const url = (event.currentTarget as HTMLElement).dataset.url ?? "";
            if (url) void this.#guard(() => { void this.#inspector.open(url, "Menu group settings"); });
        });
        this.#q("[data-cms-group-edit]")?.addEventListener("click", (event) => {
            const url = (event.currentTarget as HTMLElement).dataset.url ?? "";
            if (url) void this.#guard(() => { void this.#inspector.open(url, "Menu group settings"); });
        });
        this.#q("[data-cms-history-open]")?.addEventListener("click", () => {
            void this.#openHistory();
        });

        this.#root.addEventListener("input", (event) => this.#onSearchInput(event));
        this.#root.addEventListener("click", (event) => this.#onDelegatedClick(event));

        this.#root.querySelectorAll<HTMLElement>("[data-cms-locale]").forEach((btn) => {
            btn.addEventListener("click", () => this.#setLocale(btn.dataset.locale ?? "ko"));
        });

        this.#q<HTMLAnchorElement>("[data-cms-preview]")?.addEventListener("click", (event) => {
            if (this.#pageId > 0 && this.#tplPreview) return;
            event.preventDefault();
            window.alert("미리보기할 페이지를 먼저 선택하세요.");
        });
    }

    #onDelegatedClick(event: MouseEvent): void {
        const target = event.target as HTMLElement;

        const openPage = target.closest<HTMLElement>("[data-cms-open-page]");
        if (openPage && this.#root.contains(openPage)) {
            event.preventDefault();
            const pageId = openPage.dataset.cmsPageId ? parseInt(openPage.dataset.cmsPageId, 10) : 0;
            if (pageId > 0) void this.#guard(() => { void this.#loadCanvas(pageId); });
            return;
        }

        const inspect = target.closest<HTMLElement>("[data-cms-inspect]");
        if (inspect && this.#root.contains(inspect)) {
            event.preventDefault();
            const url = inspect.dataset.url ?? "";
            const title = inspect.dataset.title ?? "Settings";
            const pageId = inspect.dataset.cmsPageId ? parseInt(inspect.dataset.cmsPageId, 10) : 0;
            void this.#guard(() => {
                if (pageId > 0) void this.#loadCanvas(pageId);
                else this.#selectMenuItem(inspect);
                if (url) void this.#inspector.open(url, title);
            });
            return;
        }

        const addSectionTemplate = target.closest<HTMLElement>("[data-cms-add-section-template]");
        if (addSectionTemplate && this.#root.contains(addSectionTemplate)) {
            event.preventDefault();
            const url = addSectionTemplate.dataset.url ?? "";
            if (!url) return;
            void this.#pickTemplateComponent().then(async (slug) => {
                if (slug === null) return null;
                const json = await this.#actionClient.sendForm(url, {
                    template_slug: slug,
                });
                if (json === null) return;
                const id = typeof json.section_id === "number" ? String(json.section_id) : String(json.section_id ?? "");
                await this.#reloadCanvas(id !== "" ? { type: "section", id } : undefined);
                if (json.edit_url && typeof json.edit_url === "string") {
                    void this.#inspector.open(json.edit_url, "템플릿 섹션 설정");
                }
                void this.#reloadTree();
            });
            return;
        }

        const pageAdd = target.closest<HTMLElement>("[data-cms-page-add]");
        if (pageAdd && this.#root.contains(pageAdd)) {
            event.preventDefault();
            const url = pageAdd.dataset.url ?? "";
            if (url) void this.#guard(() => { void this.#inspector.open(url, "새 페이지"); });
            return;
        }

        const addElement = target.closest<HTMLElement>("[data-cms-add-element]");
        if (addElement && this.#root.contains(addElement)) {
            event.preventDefault();
            const url = addElement.dataset.url ?? "";
            if (!url) return;
            void this.#pickElementType().then((type) => {
                if (type === null) return null;
                return this.#actionClient.sendForm(url, {
                    element_type: type,
                    section_id: addElement.dataset.sectionId ?? "",
                });
            }).then((json) => {
                if (json === null) return;
                const id = typeof json.element_id === "number" ? String(json.element_id) : String(json.element_id ?? "");
                void this.#reloadCanvas(id !== "" ? { type: "element", id } : undefined);
                if (json.edit_url && typeof json.edit_url === "string") {
                    void this.#inspector.open(json.edit_url, "요소 설정");
                }
                void this.#reloadTree();
            });
            return;
        }

        const action = target.closest<HTMLElement>("[data-cms-action]");
        if (action && this.#root.contains(action)) {
            event.preventDefault();
            const kind = action.dataset.cmsAction ?? "post";
            const url = action.dataset.url ?? "";
            if (!url) return;
            if (kind === "delete") {
                void this.#actionClient.confirmAndSendJson(url, "DELETE", action.dataset.confirm ?? "Delete?")
                    .then((json) => {
                        if (json !== null) {
                            this.#showUndoToast(json, "삭제되었습니다.");
                            void this.#reloadTree();
                            void this.#reloadCanvas();
                        }
                    });
            } else {
                void this.#actionClient.sendForm(url, {}, "POST").then((json) => {
                    if (json !== null) {
                        this.#showUndoToast(json, "완료되었습니다.");
                        void this.#reloadTree();
                        void this.#reloadCanvas();
                    }
                });
            }
        }
    }

    #onSearchInput(event: Event): void {
        const target = event.target as HTMLElement;
        if (!target.matches("[data-cms-tree-search], [data-cms-page-search]")) return;
        const q = (target as HTMLInputElement).value.trim().toLowerCase();
        const selector = target.matches("[data-cms-page-search]") ? "[data-cms-page-item]" : "[data-cms-tree-item]";
        this.#root.querySelectorAll<HTMLElement>(selector).forEach((row) => {
            row.hidden = q !== "" && !(row.dataset.search ?? "").includes(q);
        });
    }

    async #handleShellMenuAction(item: CmsMenuItem, trigger: HTMLElement): Promise<void> {
        if (item.intent === "link" && item.url) {
            window.location.assign(item.url);
            return;
        }
        if (item.intent === "inspect" && item.url) {
            const pageId = trigger.dataset.cmsPageId ? parseInt(trigger.dataset.cmsPageId, 10) : 0;
            await this.#guard(() => {
                if (pageId > 0) void this.#loadCanvas(pageId);
                void this.#inspector.open(item.url ?? "", item.title ?? item.label);
            });
            return;
        }
        if (item.intent === "delete" && item.url) {
            const json = await this.#actionClient.confirmAndSendJson(item.url, "DELETE", item.confirm ?? "삭제할까요?");
            if (json !== null) {
                this.#showUndoToast(json, "삭제되었습니다.");
                await this.#reloadTree();
                await this.#reloadCanvas();
            }
            return;
        }
        if (item.intent === "post" && item.url) {
            const json = await this.#actionClient.sendForm(item.url, item.payload ?? {}, "POST");
            if (json !== null) {
                this.#showUndoToast(json, "완료되었습니다.");
                await this.#reloadTree();
                await this.#reloadCanvas();
            }
        }
    }

    async #openHistory(): Promise<void> {
        if (this.#historyUrl === "") {
            await Modal.alert("변경 이력 경로가 설정되지 않았습니다.");
            return;
        }

        const html = await this.#fetchHtml(this.#withLocale(this.#historyUrl));
        if (html === null) {
            await Modal.alert("변경 이력을 불러오지 못했습니다.");
            return;
        }

        const pending = Modal.content(html, "변경 이력", "wide");
        window.setTimeout(() => {
            const root = document.getElementById("app-modal-root");
            root?.querySelectorAll<HTMLButtonElement>("[data-cms-restore-batch]").forEach((button) => {
                button.addEventListener("click", () => {
                    const url = button.dataset.url ?? "";
                    void this.#restoreHistory(url);
                });
            });
        }, 0);
        await pending;
    }

    async #restoreHistory(url: string): Promise<void> {
        const ok = await this.#actionClient.restore(url);
        if (!ok) return;
        await this.#reloadTree();
        await this.#reloadCanvas();
    }

    #showUndoToast(json: Record<string, unknown>, message: string): void {
        const undoUrl = typeof json.undo_url === "string" ? json.undo_url : "";
        if (undoUrl === "") return;
        Toast.success(message, {
            action: {
                label: "되돌리기",
                onClick: () => {
                    void this.#restoreHistory(undoUrl);
                },
            },
        });
    }

    async #switchGroup(menuId: number): Promise<void> {
        if (menuId <= 0) return;
        this.#menuId = menuId;
        const addBtn = this.#q<HTMLButtonElement>("[data-cms-add-item]");
        if (addBtn) {
            addBtn.disabled = false;
            addBtn.dataset.url = this.#tplItemAdd.replace("__ID__", String(menuId));
        }
        await this.#reloadTree();
    }

    async #loadCanvas(pageId: number): Promise<void> {
        if (pageId <= 0 || !this.#canvas) return;
        this.#pageId = pageId;
        this.#activeTarget = `page:${pageId}`;
        this.#root.dataset.pageId = String(pageId);
        await this.#canvas.load(pageId);
        this.#syncPreviewButton();
        this.#highlightActivePage();
    }

    /**
     * 파일 연결 등 비-페이지 메뉴 선택: 캔버스 잔상을 지우고 링크 안내 플레이스홀더를 표시한 뒤,
     * 좌측 트리에서 해당 메뉴 행을 활성으로 표시한다.
     */
    #selectMenuItem(el: HTMLElement): void {
        this.#pageId = 0;
        this.#root.dataset.pageId = "0";
        this.#activeTarget = el.dataset.cmsActiveTarget ?? "";
        this.#canvas?.showMessage(this.#linkPlaceholderHtml(el));
        this.#syncPreviewButton();
        this.#highlightActivePage();
    }

    #linkPlaceholderHtml(el: HTMLElement): string {
        const label = el.dataset.cmsLinkLabel ?? "";
        const target = el.dataset.cmsLinkTarget ?? "";
        const href = el.dataset.cmsLinkHref ?? "";
        const heading = label !== "" ? `${this.#escapeHtml(label)} 메뉴` : "메뉴 항목";
        const targetLine = target !== ""
            ? `<p class="admin-cms-builder__empty-target">연결 대상: <code>${this.#escapeHtml(target)}</code></p>`
            : "";
        const previewAction = href !== ""
            ? `<div class="admin-cms-builder__empty-actions">` +
              `<a class="admin-btn admin-btn--secondary admin-btn--sm" href="${this.#escapeHtml(href)}" target="_blank" rel="noopener">` +
              `<i class="fa-solid fa-arrow-up-right-from-square" aria-hidden="true"></i><span>공개 페이지 미리보기</span></a></div>`
            : "";
        return `<div class="admin-cms-builder__empty admin-cms-builder__empty--center">` +
            `<i class="fa-regular fa-file-code admin-cms-builder__empty-icon" aria-hidden="true"></i>` +
            `<div class="admin-cms-builder__empty-copy">` +
            `<strong>${heading}</strong>` +
            `<p>이 메뉴는 파일에 직접 연결되어 있어 빌더에서 편집할 섹션 콘텐츠가 없습니다.</p>` +
            targetLine +
            `</div>` +
            previewAction +
            `</div>`;
    }

    async #reloadCanvas(restore?: { type: string; id: string }): Promise<void> {
        if (this.#pageId <= 0 || !this.#canvas) return;
        await this.#canvas.reload(this.#pageId, restore ?? this.#canvas.selectedIdentity() ?? undefined);
    }

    async #reloadTree(): Promise<void> {
        if (this.#menuId <= 0 || !this.#tplTree) return;
        const host = this.#q("[data-cms-tree-host]");
        if (!host) return;
        const html = await this.#fetchHtml(this.#withLocale(this.#tplTree.replace("__ID__", String(this.#menuId))));
        if (html === null) return;
        host.innerHTML = html;
        initCmsMenuTree();
        this.#highlightActivePage();
    }

    async #guard(run: () => void): Promise<void> {
        await this.#inspector.guard(run);
    }

    #setLocale(locale: string): void {
        this.#activeLocale = locale;
        sessionStorage.setItem("cms.builder.locale", locale);
        this.#applyLocaleButtons();
        this.#inspector.setLocale(locale);
        void this.#reloadTree();
        void this.#reloadCanvas();
    }

    #applyLocaleButtons(): void {
        this.#root.querySelectorAll<HTMLElement>("[data-cms-locale]").forEach((button) => {
            button.classList.toggle("is-active", button.dataset.locale === this.#activeLocale);
        });
    }

    #syncPreviewButton(): void {
        const preview = this.#q<HTMLAnchorElement>("[data-cms-preview]");
        if (!preview) return;

        const enabled = this.#pageId > 0 && this.#tplPreview !== "";
        preview.classList.toggle("is-disabled", !enabled);
        preview.setAttribute("aria-disabled", enabled ? "false" : "true");
        preview.href = enabled ? this.#tplPreview.replace("__ID__", String(this.#pageId)) : "#";
        preview.title = enabled ? "선택한 페이지 미리보기" : "미리보기할 페이지를 먼저 선택하세요.";
    }

    #highlightActivePage(): void {
        const key = this.#activeTarget;
        this.#root.querySelectorAll<HTMLElement>("[data-cms-active-target]").forEach((el) => {
            const on = key !== "" && el.dataset.cmsActiveTarget === key;
            el.classList.toggle("is-active-page", on);
            el.closest(".admin-page-tree__row")?.classList.toggle("is-active-page", on);
        });
    }

    #builderUrl(): string {
        const i = this.#tplCanvas.indexOf("/builder");
        if (i >= 0) return this.#tplCanvas.slice(0, i) + "/builder";

        const currentPath = window.location.pathname.replace(/\/+$/, "");
        return currentPath.includes("/builder")
            ? currentPath.slice(0, currentPath.indexOf("/builder")) + "/builder"
            : currentPath;
    }

    async #fetchHtml(url: string): Promise<string | null> {
        try {
            const res = await fetch(url, { headers: { "X-Requested-With": "XMLHttpRequest" } });
            if (!res.ok) {
                console.error(`CMS builder fragment request failed: ${res.status} ${res.statusText}`, url);
                return null;
            }
            return await res.text();
        } catch (error) {
            console.error("CMS builder fragment request failed.", error);
            return null;
        }
    }

    async #pickTemplateComponent(): Promise<string | null> {
        if (this.#componentPickerUrl === "") {
            await Modal.alert("템플릿 컴포넌트 선택기가 설정되지 않았습니다.");
            return null;
        }
        const html = await this.#fetchHtml(this.#componentPickerUrl);
        if (html === null) {
            await Modal.alert("템플릿 컴포넌트 목록을 불러오지 못했습니다.");
            return null;
        }

        const pending = Modal.content(html, "템플릿 컴포넌트 선택", "wide");
        return await new Promise((resolve) => {
            window.setTimeout(() => {
                const root = document.getElementById("app-modal-root");
                const picker = root?.querySelector<HTMLElement>("[data-template-component-picker]");
                if (!picker) {
                    resolve(null);
                    return;
                }
                const close = root?.querySelector<HTMLButtonElement>("[data-app-modal-close]");
                root?.querySelector<HTMLButtonElement>("[data-app-modal-ok]")?.replaceChildren("닫기");
                initCmsTemplatePicker(picker, (slug) => {
                    close?.click();
                    resolve(slug !== "" ? slug : null);
                });
            }, 0);
            void pending.then(() => resolve(null));
        });
    }

    async #pickElementType(): Promise<string | null> {
        const entries = Object.entries(this.#elementTypes);
        if (entries.length === 0) {
            await Modal.alert("추가할 수 있는 요소 유형이 없습니다.");
            return null;
        }

        const cards = entries.map(([type, label]) => {
            const icon = this.#elementTypeMeta[type]?.icon ?? "fa-solid fa-cube";
            return `<button type="button" class="admin-cms-template-picker__card admin-cms-template-picker__card--element" data-cms-element-type-choice data-element-type="${this.#escapeHtml(type)}" data-search="${this.#escapeHtml(`${type} ${label}`.toLowerCase())}">` +
                `<span class="admin-cms-template-picker__thumb admin-cms-template-picker__thumb--icon"><i class="${this.#escapeHtml(icon)}" aria-hidden="true"></i></span>` +
                `<span class="admin-cms-template-picker__meta"><strong>${this.#escapeHtml(label)}</strong><code>${this.#escapeHtml(type)}</code></span>` +
                `<span class="admin-cms-template-picker__action" aria-hidden="true"><i class="fa-solid fa-arrow-right"></i></span>` +
                `</button>`;
        }).join("");

        const html = `<div class="admin-cms-template-picker" data-cms-element-type-picker data-cms-element-type-count="${entries.length}">` +
            `<div class="admin-cms-template-picker__summary"><div><span class="admin-cms-template-picker__eyebrow">콘텐츠 요소</span><p>페이지에 넣을 요소를 선택하면 바로 추가됩니다.</p></div><span class="admin-badge admin-badge--default">${entries.length}개</span></div>` +
            `<div class="admin-cms-template-picker__search"><label class="admin-form__label" for="cms-element-type-search">검색</label><input type="text" id="cms-element-type-search" class="admin-form__input" placeholder="텍스트, 이미지, HTML..." data-cms-element-type-search></div>` +
            `<div class="admin-cms-template-picker__groups" data-lenis-prevent><section class="admin-cms-template-picker__group"><h3 class="admin-cms-template-picker__group-title">전체 요소</h3><div class="admin-cms-template-picker__grid">${cards}</div><p class="admin-cms-template-picker__empty" data-cms-element-type-empty hidden>검색 결과가 없습니다.</p></section></div>` +
            `</div>`;

        const pending = Modal.content(html, "요소 추가", "wide");
        return await new Promise((resolve) => {
            window.setTimeout(() => {
                const root = document.getElementById("app-modal-root");
                const picker = root?.querySelector<HTMLElement>("[data-cms-element-type-picker]");
                if (!picker) {
                    resolve(null);
                    return;
                }
                const close = root?.querySelector<HTMLButtonElement>("[data-app-modal-close]");
                const search = picker.querySelector<HTMLInputElement>("[data-cms-element-type-search]");
                search?.addEventListener("input", () => {
                    const q = search.value.trim().toLowerCase();
                    let visible = 0;
                    picker.querySelectorAll<HTMLElement>("[data-cms-element-type-choice]").forEach((card) => {
                        const matched = q === "" || (card.dataset.search ?? "").includes(q);
                        card.hidden = !matched;
                        if (matched) visible += 1;
                    });
                    const empty = picker.querySelector<HTMLElement>("[data-cms-element-type-empty]");
                    if (empty) empty.hidden = visible > 0;
                });
                picker.addEventListener("click", (event) => {
                    const choice = (event.target as HTMLElement | null)?.closest<HTMLElement>("[data-cms-element-type-choice]");
                    if (!choice) return;
                    event.preventDefault();
                    const type = choice.dataset.elementType ?? "";
                    close?.click();
                    resolve(type !== "" ? type : null);
                });
            }, 0);
            void pending.then(() => resolve(null));
        });
    }

    #withLocale(url: string): string {
        const glue = url.includes("?") ? "&" : "?";
        return `${url}${glue}locale=${encodeURIComponent(this.#activeLocale)}`;
    }

    #q<T extends HTMLElement = HTMLElement>(sel: string): T | null {
        return this.#root.querySelector<T>(sel);
    }

    #parseElementTypes(raw: string): Record<string, string> {
        try {
            const parsed = JSON.parse(raw) as Record<string, unknown>;
            const out: Record<string, string> = {};
            Object.entries(parsed).forEach(([key, value]) => {
                if (typeof value === "string") out[key] = value;
            });
            return out;
        } catch {
            return {};
        }
    }

    #filterElementTypes(types: Record<string, string>): Record<string, string> {
        const filtered: Record<string, string> = {};
        Object.entries(types).forEach(([key, label]) => {
            if (key === "template_field") return;
            filtered[key] = label;
        });
        return filtered;
    }

    #escapeHtml(value: string): string {
        return value
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/"/g, "&quot;");
    }

    #parseElementTypeMeta(raw: string): Record<string, CmsElementTypeMeta> {
        try {
            const parsed = JSON.parse(raw) as Record<string, unknown>;
            const out: Record<string, CmsElementTypeMeta> = {};
            Object.entries(parsed).forEach(([key, value]) => {
                if (typeof value !== "object" || value === null) return;
                const row = value as Record<string, unknown>;
                const icon = typeof row.icon === "string" ? row.icon : "";
                if (!icon) return;
                out[key] = {
                    icon,
                    group: typeof row.group === "string" ? row.group : undefined,
                };
            });
            return out;
        } catch {
            return {};
        }
    }
}

interface BuilderRoot extends HTMLElement { _cmsBuilderInited?: boolean; }

export function initCmsSiteBuilder(): void {
    const root = document.querySelector<BuilderRoot>("[data-cms-builder]");
    if (!root || root._cmsBuilderInited) return;
    root._cmsBuilderInited = true;
    new CmsSiteBuilder(root).init();
}
